/**
 * map Block JavaScript
 */

(function () {
  "use strict";

  let leafletPromise = null;

  function loadLeaflet({ cssUrl, jsUrl } = {}) {
    if (window.L) {
      return Promise.resolve(window.L);
    }

    if (!leafletPromise) {
      leafletPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[data-leaflet]`);
        if (existingScript && window.L) {
          resolve(window.L);
          return;
        }

        if (!cssUrl || !jsUrl) {
          reject(new Error("[BLOCK][map] Missing Leaflet asset URLs"));
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = cssUrl;
        link.setAttribute("data-leaflet", "true");
        document.head.appendChild(link);

        const script = document.createElement("script");
        script.src = jsUrl;
        script.async = true;
        script.defer = true;
        script.setAttribute("data-leaflet", "true");
        script.onload = () => resolve(window.L);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    return leafletPromise;
  }

  const NOMINATIM_ENDPOINT =
    "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=";

  function geocodeAddress(address) {
    if (!address) {
      return Promise.resolve(null);
    }

    const url = NOMINATIM_ENDPOINT + encodeURIComponent(address);
    return fetch(url, {
      headers: {
        "Accept-Language": document.documentElement.lang || "en",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Geocoding request failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!data.length) {
          return null;
        }
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
      })
      .catch((error) => {
        console.error("[BLOCK][map] Geocoding error", error);
        return null;
      });
  }

  function normalizePoints(points = []) {
    return points
      .map((point) => {
        if (!point) {
          return null;
        }

        if (point.locations) {
          const location = point.locations || {};
          const normalized = {
            ...location,
            title: point.title || location.name || "",
            address: location.address || point.address || "",
          };

          if (normalized.lat) {
            normalized.lat = parseFloat(normalized.lat);
          }
          if (normalized.lng) {
            normalized.lng = parseFloat(normalized.lng);
          }

          return normalized;
        }

        if (point.lat) {
          point.lat = parseFloat(point.lat);
        }
        if (point.lng) {
          point.lng = parseFloat(point.lng);
        }

        return point;
      })
      .filter(Boolean);
  }

  function enrichPoints(points) {
    const prepared = [];

    const tasks = points.map((point) => {
      if (point.lat && point.lng) {
        prepared.push(point);
        return Promise.resolve();
      }

      if (point.address) {
        return geocodeAddress(point.address).then((coords) => {
          if (coords) {
            prepared.push({
              ...point,
              lat: coords.lat,
              lng: coords.lng,
            });
          }
        });
      }

      return Promise.resolve();
    });

    return Promise.all(tasks).then(() => prepared);
  }

  function initLeafletMap(mapElement) {
    if (!mapElement) {
      return;
    }

    let points = [];
    try {
      points = JSON.parse(mapElement.dataset.map || "[]");
    } catch (error) {
      console.error("[BLOCK][map] Failed to parse map data", error);
    }

    points = normalizePoints(points);

    const height = mapElement.dataset.height || "534px";
    mapElement.style.height = height;

    const assets = {
      cssUrl: mapElement.dataset.leafletCss,
      jsUrl: mapElement.dataset.leafletJs,
    };

    Promise.all([loadLeaflet(assets), enrichPoints(points)])
      .then(([L, enrichedPoints]) => {
        const validPoints = enrichedPoints.filter(
          (point) => point.lat && point.lng
        );

        if (!validPoints.length) {
          mapElement.classList.add("map__map--empty");
          mapElement.textContent = "Map data unavailable";
          return;
        }

        const map = L.map(mapElement).setView(
          [validPoints[0].lat, validPoints[0].lng],
          8
        );

        // Use a tile style - labels are baked into raster tiles
        // To hide labels, we'd need to use a different tile provider or custom tiles
        // For now, we'll use the existing style and apply CSS to reduce label visibility
        const apiKey = mapElement.dataset.stadiaApiKey || '';
        let tileUrl = "https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.{ext}";
        
        // Add API key if provided
        if (apiKey) {
          tileUrl += "?api_key=" + encodeURIComponent(apiKey);
        }
        
        const tileLayer = L.tileLayer(
          tileUrl,
          {
            minZoom: 5,
            maxZoom: 20,
            ext: "png",
            attribution:
              '&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          }
        ).addTo(map);

        // Apply CSS to map container to help reduce label visibility
        // Note: This won't completely remove labels as they're part of the raster images
        const mapContainer = map.getContainer();
        if (mapContainer) {
          mapContainer.classList.add('map-hide-labels');
        }

        const icon = L.icon({
          iconUrl: mapElement.dataset.pin,
          iconSize: [40, 54],
          iconAnchor: [20, 54],
          popupAnchor: [0, -54],
        });

        validPoints.forEach((point) => {
          const marker = L.marker([point.lat, point.lng], { icon }).addTo(map);
          const popupContent = `<strong>${point.title || ""}</strong>`;
          marker.bindPopup(popupContent.trim());
        });

        // Fit all markers in view
        if (validPoints.length > 1) {
          const bounds = L.latLngBounds(
            validPoints.map((point) => [point.lat, point.lng])
          );
          // Remove maxZoom to allow full zoom out for global markers
          map.fitBounds(bounds, { padding: [50, 50] });
        } else {
          map.setView([validPoints[0].lat, validPoints[0].lng], 8);
        }
      })
      .catch((error) => {
        console.error("[BLOCK][map] Failed to load Leaflet", error);
      });
  }

  function initMapBlocks() {
    const blocks = document.querySelectorAll(".map .js-leaflet-map");
    blocks.forEach(initLeafletMap);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMapBlocks);
  } else {
    initMapBlocks();
  }
})();
